export type CrewJobMapKind = 'jobsite' | 'hotel';

export type CrewJobMapPin = {
  id: CrewJobMapKind;
  label: string;
  address: string;
  lat: number;
  lng: number;
};

export type CrewJobMapSource = {
  location?: string | null;
  locationLat?: number | null;
  locationLng?: number | null;
  hotelName?: string | null;
  hotelLocation?: string | null;
  hotelLat?: number | null;
  hotelLng?: number | null;
};

export function optionalCrewText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

export function optionalCrewCoord(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export function formatCrewJobHotelLine(
  hotelName?: string | null,
  hotelLocation?: string | null,
): string | null {
  const name = optionalCrewText(hotelName);
  const loc = optionalCrewText(hotelLocation);
  if (!name && !loc) return null;
  if (name && loc) return `${name} · ${loc}`;
  return name || loc;
}

export function crewJobHasHotel(job: Pick<CrewJobMapSource, 'hotelName' | 'hotelLocation'>): boolean {
  return Boolean(formatCrewJobHotelLine(job.hotelName, job.hotelLocation));
}

export function crewJobHasMapletCopy(job: CrewJobMapSource): boolean {
  return Boolean(optionalCrewText(job.location) || crewJobHasHotel(job));
}

export function buildCrewJobMapPins(job: CrewJobMapSource): CrewJobMapPin[] {
  const pins: CrewJobMapPin[] = [];
  const siteLat = optionalCrewCoord(job.locationLat);
  const siteLng = optionalCrewCoord(job.locationLng);
  const siteAddress = optionalCrewText(job.location);
  if (siteLat != null && siteLng != null && siteAddress) {
    pins.push({
      id: 'jobsite',
      label: 'Jobsite',
      address: siteAddress,
      lat: siteLat,
      lng: siteLng,
    });
  }

  const hotelLine = formatCrewJobHotelLine(job.hotelName, job.hotelLocation);
  const hotelLat = optionalCrewCoord(job.hotelLat);
  const hotelLng = optionalCrewCoord(job.hotelLng);
  if (hotelLine && hotelLat != null && hotelLng != null) {
    pins.push({
      id: 'hotel',
      label: optionalCrewText(job.hotelName) || 'Hotel',
      address: optionalCrewText(job.hotelLocation) || hotelLine,
      lat: hotelLat,
      lng: hotelLng,
    });
  }

  return pins;
}

export function googleMapsSearchUrl(query: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

export const CREW_JOB_MAP_STYLES: google.maps.MapTypeStyle[] = [
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { elementType: 'geometry', stylers: [{ saturation: -28 }, { lightness: 6 }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#4a5a4c' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#f7f1e8' }] },
  { featureType: 'water', stylers: [{ color: '#c5d4c8' }] },
  { featureType: 'road', stylers: [{ saturation: -45 }] },
  { featureType: 'landscape.man_made', stylers: [{ saturation: -20 }] },
];
